import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { User, AuthContextType, RegisterData } from '../types';

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    const initAuth = async () => {
      // Add a small delay to ensure localStorage is available
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      // Check if we have user data in localStorage
      if (storedUser && storedUser !== 'null' && storedUser !== 'undefined') {
        try {
          const userData = JSON.parse(storedUser);
          
          // Validate that we have the required fields
          if (userData && userData.firstName && userData.lastName && userData.email) {
            // Ensure the user data has the correct structure
            const normalizedUserData = {
              _id: userData._id || userData.id, // Handle both _id and id
              firstName: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
              company: userData.company || '',
              phone: userData.phone || '',
              avatar: userData.avatar || '',
              createdAt: userData.createdAt || new Date().toISOString(),
              updatedAt: userData.updatedAt || new Date().toISOString()
            };
            
            setUser(normalizedUserData);
            
            // Set token if available
            if (storedToken && storedToken !== 'null' && storedToken !== 'undefined') {
              setToken(storedToken);
              axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            }
            
            // Optionally verify with backend (but don't fail if it doesn't work)
            try {
              await axios.get('/api/auth/me');
            } catch (error) {
              // If /me endpoint fails, we'll still use the stored user data
            }
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
          }
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
        }
      } else {
        // Clear any invalid data
        if (localStorage.getItem('user') === 'null' || localStorage.getItem('user') === 'undefined') {
          localStorage.removeItem('user');
        }
        if (localStorage.getItem('token') === 'null' || localStorage.getItem('token') === 'undefined') {
          localStorage.removeItem('token');
        }
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password
      });

      // Handle the response structure from auth-mysql.js
      const rawUserData = response.data.data?.user || response.data.user;
      const token = response.data.data?.token || response.data.token;
      
      // Transform user data to match the frontend User interface
      const userData = {
        _id: rawUserData.id, // Convert 'id' to '_id'
        firstName: rawUserData.firstName,
        lastName: rawUserData.lastName,
        email: rawUserData.email,
        company: rawUserData.company,
        phone: rawUserData.phone,
        avatar: rawUserData.avatar || '',
        createdAt: rawUserData.createdAt,
        updatedAt: rawUserData.updatedAt
      };
      
      // Use the real JWT token from the backend
      if (token) {
        setToken(token);
        setUser(userData);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else {
        throw new Error('No token received from server');
      }
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);

      // Handle the response structure from auth-mysql.js
      const rawUserData = response.data.data?.user || response.data.user;
      const token = response.data.data?.token || response.data.token;
      
      // Transform user data to match the frontend User interface
      const newUser = {
        _id: rawUserData.id, // Convert 'id' to '_id'
        firstName: rawUserData.firstName,
        lastName: rawUserData.lastName,
        email: rawUserData.email,
        company: rawUserData.company,
        phone: rawUserData.phone,
        avatar: rawUserData.avatar || '',
        createdAt: rawUserData.createdAt,
        updatedAt: rawUserData.updatedAt
      };
      
      // Use the real JWT token from the backend
      if (token) {
        setToken(token);
        setUser(newUser);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(newUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else {
        throw new Error('No token received from server');
      }

      toast.success('Registration successful!');
      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const updateProfile = async (data: Partial<User> | FormData) => {
    try {
      let response;
      if (typeof FormData !== 'undefined' && data instanceof FormData) {
        response = await axios.put('/api/users/profile', data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.put('/api/users/profile', data);
      }
      setUser(response.data.user);
      // Update localStorage with new user data
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Profile updated successfully!');
      return { success: true, user: response.data.user };
    } catch (error: any) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 